import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, where, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';

interface Tournament {
  id: string;
  name: string;
  description?: string;

  // Type & Status
  type: 'online' | 'private' | 'club';
  status: 'upcoming' | 'ongoing' | 'completed' | 'cancelled';

  // Dates
  startDate: Date;
  endDate: Date;
  createdAt: Date;

  // Participants
  maxParticipants: number;
  participantIds: string[];
  bannedUsers: string[];

  // Creator Info
  createdBy: string;
  createdByRole: 'admin' | 'operator';

  // Game Settings
  gameMode: '301' | '501' | '701' | 'cricket';
  doubleOut: boolean;
  doubleIn: boolean;
  randomCricket: boolean;

  // Bracket Settings
  bracketType: 'singleElimination' | 'doubleElimination';
  bracket: any | null;

  // Venue Info (for club tournaments)
  isVenueEvent?: boolean;
  venueId?: string;
  venueName?: string;

  // Prizes (optional)
  prizePoolAmount?: number;
  firstPrize?: string;
  secondPrize?: string;
  thirdPrize?: string;

  // Public/Private
  isPublic: boolean;
}

interface User {
  uid: string;
  displayName: string;
  email: string;
  role: string;
}

interface Venue {
  id: string;
  name: string;
  address: string;
  status: string;
}

interface TournamentListProps {
  onSelectTournament?: (tournament: { id: string; name: string; type: 'single_elimination' | 'double_elimination' | 'round_robin' | 'swiss' }) => void;
}

const TournamentList: React.FC<TournamentListProps> = ({ onSelectTournament }) => {
  const { t } = useTranslation();
  const { user, isAdmin, isVenueOperator } = useAuth();
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Tournament | null>(null);
  const [organizers, setOrganizers] = useState<User[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [form, setForm] = useState({
    name: '',
    description: '',
    type: 'online' as Tournament['type'],
    gameMode: '501' as Tournament['gameMode'],
    status: 'upcoming' as Tournament['status'],
    maxParticipants: 16,
    startDate: '',
    endDate: '',
    venueId: '',
    venueName: '',
    prizePoolAmount: 0,
    firstPrize: '',
    secondPrize: '',
    thirdPrize: '',
    doubleOut: true,
    doubleIn: false,
    randomCricket: false,
    bracketType: 'singleElimination' as 'singleElimination' | 'doubleElimination',
    isPublic: true,
    createdBy: '',
    createdByRole: 'admin' as 'admin' | 'operator'
  });

  useEffect(() => {
    loadTournaments();
    loadOrganizers();
    loadVenues();
  }, []);

  const loadTournaments = async () => {
    try {
      setLoading(true);
      const selectedVenueId = localStorage.getItem('selectedVenueId');

      let q;
      if (isVenueOperator && !isAdmin && selectedVenueId) {
        // Üzemeltetők csak a saját helyszínük versenyeit látják
        q = query(
          collection(db, 'tournaments'),
          where('venueId', '==', selectedVenueId),
          orderBy('startDate', 'desc')
        );
      } else {
        // Adminok mindent látnak
        q = query(collection(db, 'tournaments'), orderBy('startDate', 'desc'));
      }

      const snapshot = await getDocs(q);

      const tournamentsData = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          startDate: data.startDate?.toDate(),
          endDate: data.endDate?.toDate(),
          createdAt: data.createdAt?.toDate(),
          participantIds: data.participantIds || [],
          bannedUsers: data.bannedUsers || []
        } as Tournament;
      });

      setTournaments(tournamentsData);
    } catch (error) {
      console.error('Error loading tournaments:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizers = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const adminAndOperators: User[] = [];

      usersSnapshot.forEach((doc) => {
        const data = doc.data();
        const role = data.role || '';

        if (role === 'admin' || role === 'venue_operator') {
          adminAndOperators.push({
            uid: doc.id,
            displayName: data.displayName || data.email || 'Névtelen',
            email: data.email || '',
            role: role
          });
        }
      });

      setOrganizers(adminAndOperators);
    } catch (error) {
      console.error('Error loading organizers:', error);
    }
  };

  const loadVenues = async () => {
    try {
      const venuesSnapshot = await getDocs(collection(db, 'venues'));
      const activeVenues: Venue[] = [];

      venuesSnapshot.forEach((doc) => {
        const data = doc.data();
        const status = data.status || '';

        if (status === 'active') {
          activeVenues.push({
            id: doc.id,
            name: data.name || 'Névtelen helyszín',
            address: data.address || '',
            status: status
          });
        }
      });

      setVenues(activeVenues);
    } catch (error) {
      console.error('Error loading venues:', error);
    }
  };

  const handleSave = async () => {
    if (!form.name || !form.startDate || !form.endDate) {
      alert('Kérlek töltsd ki a kötelező mezőket!');
      return;
    }

    if (new Date(form.startDate) >= new Date(form.endDate)) {
      alert('A befejezési dátum későbbi kell legyen, mint a kezdő dátum!');
      return;
    }

    // Üzemeltetőknél kötelező a helyszín
    if (isVenueOperator && !isAdmin && !form.venueId) {
      alert('Kérlek válassz helyszínt!');
      return;
    }

    try {
      const tournamentData = {
        name: form.name,
        description: form.description || '',
        type: form.type,
        status: form.status,
        startDate: Timestamp.fromDate(new Date(form.startDate)),
        endDate: Timestamp.fromDate(new Date(form.endDate)),
        maxParticipants: form.maxParticipants,
        participantIds: editing ? editing.participantIds : [],
        bannedUsers: editing ? editing.bannedUsers : [],
        createdBy: user?.uid || '',
        createdByRole: isAdmin ? 'admin' : 'operator',
        gameMode: form.gameMode,
        doubleOut: form.doubleOut,
        doubleIn: form.doubleIn,
        randomCricket: form.randomCricket,
        bracketType: form.bracketType,
        bracket: null,
        isVenueEvent: form.type === 'club',
        venueId: form.type === 'club' ? form.venueId : undefined,
        venueName: form.type === 'club' ? form.venueName : undefined,
        prizePoolAmount: form.prizePoolAmount || 0,
        firstPrize: form.firstPrize || '',
        secondPrize: form.secondPrize || '',
        thirdPrize: form.thirdPrize || '',
        isPublic: form.type === 'online' || form.type === 'private'
      };

      if (editing) {
        await updateDoc(doc(db, 'tournaments', editing.id), tournamentData);
      } else {
        await addDoc(collection(db, 'tournaments'), {
          ...tournamentData,
          createdAt: Timestamp.now()
        });
      }

      setShowModal(false);
      setEditing(null);
      resetForm();
      loadTournaments();
    } catch (error) {
      console.error('Error saving tournament:', error);
      alert('Hiba történt a mentés során!');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('tournament.confirmDelete'))) {
      try {
        await deleteDoc(doc(db, 'tournaments', id));
        loadTournaments();
      } catch (error) {
        console.error('Error deleting tournament:', error);
      }
    }
  };

  const handleStartTournament = async (tournament: Tournament) => {
    if (tournament.participantIds.length < 2) {
      alert('Legalább 2 résztvevő szükséges a verseny indításához!');
      return;
    }

    if (window.confirm(`Biztosan elindítod a "${tournament.name}" versenyt? A résztvevők értesítést kapnak.`)) {
      try {
        await updateDoc(doc(db, 'tournaments', tournament.id), {
          status: 'ongoing',
          startedAt: Timestamp.now()
        });

        alert('✅ Verseny sikeresen elindítva! A résztvevők értesítést kaptak.');
        loadTournaments();
      } catch (error) {
        console.error('Error starting tournament:', error);
        alert('Hiba történt a verseny indítása során!');
      }
    }
  };

  const handleEdit = (tournament: Tournament) => {
    setEditing(tournament);
    setForm({
      name: tournament.name,
      description: tournament.description || '',
      type: tournament.type,
      gameMode: tournament.gameMode,
      status: tournament.status,
      maxParticipants: tournament.maxParticipants,
      startDate: tournament.startDate.toISOString().split('T')[0],
      endDate: tournament.endDate.toISOString().split('T')[0],
      venueId: tournament.venueId || '',
      venueName: tournament.venueName || '',
      prizePoolAmount: tournament.prizePoolAmount || 0,
      firstPrize: tournament.firstPrize || '',
      secondPrize: tournament.secondPrize || '',
      thirdPrize: tournament.thirdPrize || '',
      doubleOut: tournament.doubleOut,
      doubleIn: tournament.doubleIn,
      randomCricket: tournament.randomCricket,
      bracketType: tournament.bracketType,
      isPublic: tournament.isPublic,
      createdBy: tournament.createdBy,
      createdByRole: tournament.createdByRole
    });
    setShowModal(true);
  };

  const resetForm = () => {
    const selectedVenueId = localStorage.getItem('selectedVenueId');
    const selectedVenue = venues.find(v => v.id === selectedVenueId);

    setForm({
      name: '',
      description: '',
      type: isVenueOperator && !isAdmin ? 'club' : 'online',
      gameMode: '501',
      status: 'upcoming',
      maxParticipants: 16,
      startDate: '',
      endDate: '',
      venueId: isVenueOperator && !isAdmin ? (selectedVenueId || '') : '',
      venueName: isVenueOperator && !isAdmin ? (selectedVenue?.name || '') : '',
      prizePoolAmount: 0,
      firstPrize: '',
      secondPrize: '',
      thirdPrize: '',
      doubleOut: true,
      doubleIn: false,
      randomCricket: false,
      bracketType: 'singleElimination',
      isPublic: true,
      createdBy: '',
      createdByRole: 'admin'
    });
  };

  const getStatusColor = (status: Tournament['status']) => {
    switch (status) {
      case 'upcoming': return 'bg-blue-500/20 text-blue-400';
      case 'ongoing': return 'bg-green-500/20 text-green-400';
      case 'completed': return 'bg-purple-500/20 text-purple-400';
      case 'cancelled': return 'bg-red-500/20 text-red-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getTypeIcon = (type: Tournament['type']) => {
    switch (type) {
      case 'online': return '�';
      case 'club': return '📍';
      case 'private': return '🔒';
      default: return '🎮';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-[#B8B8C3]">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-2xl font-bold text-white">{t('tournament.tournamentList')}</h3>
        <button
          onClick={() => {
            resetForm();
            setEditing(null);
            setShowModal(true);
          }}
          className="btn-primary flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('tournament.newTournament')}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {tournaments.map(tournament => (
          <div key={tournament.id} className="card-dark p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <span className="text-4xl">{getTypeIcon(tournament.type)}</span>
                <div>
                  <h4 className="text-xl font-bold text-white">{tournament.name}</h4>
                  <p className="text-sm text-[#B8B8C3]">{tournament.venueName}</p>
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(tournament.status)}`}>
                {t(`tournament.status.${tournament.status}`)}
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <p className="text-xs text-[#B8B8C3] mb-1">Típus</p>
                <p className="text-sm text-white font-semibold">
                  {tournament.type === 'online' ? 'Online' : tournament.type === 'club' ? 'Helyszín' : 'Privát'}
                </p>
              </div>
              <div>
                <p className="text-xs text-[#B8B8C3] mb-1">Játékmód</p>
                <p className="text-sm text-white font-semibold">{tournament.gameMode}</p>
              </div>
              <div>
                <p className="text-xs text-[#B8B8C3] mb-1">Résztvevők</p>
                <p className="text-sm text-white font-semibold">
                  {tournament.participantIds.length} / {tournament.maxParticipants}
                </p>
              </div>
              <div>
                <p className="text-xs text-[#B8B8C3] mb-1">Nyereményalap</p>
                <p className="text-sm text-cyan-400 font-semibold">{(tournament.prizePoolAmount || 0).toLocaleString()} Ft</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4 text-sm">
              <div className="flex items-center text-[#B8B8C3]">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Kezdés: {tournament.startDate?.toLocaleDateString() || 'N/A'}
              </div>
              <div className="flex items-center text-[#B8B8C3]">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Befejezés: {tournament.endDate?.toLocaleDateString() || 'N/A'}
              </div>
            </div>

            {tournament.description && (
              <div className="mb-4 p-3 bg-[#1d232a] rounded-lg">
                <p className="text-sm text-[#B8B8C3]">{tournament.description}</p>
              </div>
            )}

            <div className="mb-4 space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-[#B8B8C3]">Bracket típus:</span>
                <span className="text-white font-semibold">
                  {tournament.bracketType === 'singleElimination' ? 'Egyenes kieséses' : 'Dupla kieséses'}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-[#B8B8C3]">Beállítások:</span>
                <div className="flex gap-2">
                  {tournament.doubleOut && <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded text-xs">Double Out</span>}
                  {tournament.doubleIn && <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded text-xs">Double In</span>}
                  {tournament.randomCricket && <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded text-xs">Random Cricket</span>}
                </div>
              </div>
            </div>

            {(tournament.firstPrize || tournament.secondPrize || tournament.thirdPrize) && (
              <div className="mb-4 p-4 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/20 rounded-lg">
                <h5 className="text-sm font-semibold text-white mb-2">🏆 Nyeremények</h5>
                <div className="space-y-1 text-sm">
                  {tournament.firstPrize && (
                    <p className="text-yellow-400">🥇 1. helyezés: {tournament.firstPrize}</p>
                  )}
                  {tournament.secondPrize && (
                    <p className="text-gray-300">🥈 2. helyezés: {tournament.secondPrize}</p>
                  )}
                  {tournament.thirdPrize && (
                    <p className="text-orange-400">🥉 3. helyezés: {tournament.thirdPrize}</p>
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-2">
              {tournament.status === 'upcoming' && (
                <button
                  onClick={() => handleStartTournament(tournament)}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-green-400 rounded-lg hover:from-green-500/30 hover:to-emerald-500/30 transition-all text-sm font-semibold border border-green-500/30 hover:border-green-500/50"
                >
                  🚀 Verseny indítása
                </button>
              )}
              {tournament.status === 'ongoing' && (
                <div className="flex-1 px-4 py-2 bg-green-500/10 text-green-400 rounded-lg text-sm font-semibold text-center border border-green-500/30">
                  ⚡ Folyamatban
                </div>
              )}
              {onSelectTournament && (
                <button
                  onClick={() => onSelectTournament({
                    id: tournament.id,
                    name: tournament.name,
                    type: tournament.bracketType === 'singleElimination' ? 'single_elimination' : 'double_elimination'
                  })}
                  className="px-4 py-2 bg-cyan-500/10 text-cyan-400 rounded-lg hover:bg-cyan-500/20 transition-colors text-sm font-semibold border border-cyan-500/30 hover:border-cyan-500/50"
                >
                  📊 Bracket
                </button>
              )}
              <button
                onClick={() => handleEdit(tournament)}
                className="px-4 py-2 btn-secondary text-sm"
              >
                {t('common.edit')}
              </button>
              <button
                onClick={() => handleDelete(tournament.id)}
                className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors text-sm"
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        ))}
      </div>

      {tournaments.length === 0 && (
        <div className="card-dark p-12 text-center">
          <div className="text-6xl mb-4">🏆</div>
          <h3 className="text-xl font-bold text-white mb-2">{t('tournament.noTournaments')}</h3>
          <p className="text-[#B8B8C3] mb-4">{t('tournament.noTournamentsDesc')}</p>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="btn-primary"
          >
            {t('tournament.createFirst')}
          </button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card-dark p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-white mb-6">
              {editing ? t('tournament.editTournament') : t('tournament.newTournament')}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  Verseny neve *
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="input-dark w-full"
                  placeholder="Spring Championship 2026"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  Leírás
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="input-dark w-full"
                  rows={3}
                  placeholder="Részletes leírás a versenyről..."
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    Típus *
                  </label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value as Tournament['type'] })}
                    className="input-dark w-full"
                    disabled={isVenueOperator && !isAdmin}
                  >
                    {(isAdmin || !isVenueOperator) && (
                      <>
                        <option value="online">🌐 Online</option>
                        <option value="private">� Privát</option>
                      </>
                    )}
                    <option value="club">� Helyszín (Club)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    Játékmód *
                  </label>
                  <select
                    value={form.gameMode}
                    onChange={(e) => setForm({ ...form, gameMode: e.target.value as Tournament['gameMode'] })}
                    className="input-dark w-full"
                  >
                    <option value="301">301</option>
                    <option value="501">501</option>
                    <option value="701">701</option>
                    <option value="cricket">Cricket</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    Státusz *
                  </label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value as Tournament['status'] })}
                    className="input-dark w-full"
                  >
                    <option value="upcoming">Közelgő</option>
                    <option value="ongoing">Folyamatban</option>
                    <option value="completed">Befejezett</option>
                    <option value="cancelled">Törölve</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    Max. résztvevők *
                  </label>
                  <input
                    type="number"
                    value={form.maxParticipants}
                    onChange={(e) => setForm({ ...form, maxParticipants: Number(e.target.value) })}
                    className="input-dark w-full"
                    min="4"
                    step="4"
                  />
                </div>
              </div>

              {form.type === 'club' && (
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    Helyszín *
                  </label>
                  <select
                    value={form.venueId}
                    onChange={(e) => {
                      const selectedVenueId = e.target.value;
                      const selectedVenue = venues.find(v => v.id === selectedVenueId);
                      setForm({
                        ...form,
                        venueId: selectedVenueId,
                        venueName: selectedVenue?.name || ''
                      });
                    }}
                    className="input-dark w-full"
                    disabled={isVenueOperator && !isAdmin}
                  >
                    <option value="">Válassz helyszínt...</option>
                    {venues.map((venue) => (
                      <option key={venue.id} value={venue.id}>
                        📍 {venue.name}
                      </option>
                    ))}
                  </select>
                  {form.venueId && (
                    <p className="text-xs text-[#B8B8C3] mt-1">
                      {venues.find(v => v.id === form.venueId)?.address || ''}
                    </p>
                  )}
                  {isVenueOperator && !isAdmin && (
                    <p className="text-xs text-cyan-400 mt-1">
                      ℹ️ Üzemeltetőként csak a kiválasztott helyszín eseményeit hozhatod létre
                    </p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    Kezdés dátuma *
                  </label>
                  <input
                    type="date"
                    value={form.startDate}
                    onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                    className="input-dark w-full"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    Befejezés dátuma *
                  </label>
                  <input
                    type="date"
                    value={form.endDate}
                    onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                    min={form.startDate || undefined}
                    className="input-dark w-full"
                    required
                  />
                  {form.startDate && form.endDate && new Date(form.startDate) >= new Date(form.endDate) && (
                    <p className="text-red-400 text-xs mt-1">⚠️ A befejezés későbbi kell legyen</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    Bracket típus *
                  </label>
                  <select
                    value={form.bracketType}
                    onChange={(e) => setForm({ ...form, bracketType: e.target.value as 'singleElimination' | 'doubleElimination' })}
                    className="input-dark w-full"
                  >
                    <option value="singleElimination">Egyenes kieséses</option>
                    <option value="doubleElimination">Dupla kieséses</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    Nyereményalap (Ft)
                  </label>
                  <input
                    type="number"
                    value={form.prizePoolAmount}
                    onChange={(e) => setForm({ ...form, prizePoolAmount: Number(e.target.value) })}
                    className="input-dark w-full"
                    min="0"
                    step="1000"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    🥇 1. helyezés
                  </label>
                  <input
                    type="text"
                    value={form.firstPrize}
                    onChange={(e) => setForm({ ...form, firstPrize: e.target.value })}
                    className="input-dark w-full"
                    placeholder="30000 Ft"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    🥈 2. helyezés
                  </label>
                  <input
                    type="text"
                    value={form.secondPrize}
                    onChange={(e) => setForm({ ...form, secondPrize: e.target.value })}
                    className="input-dark w-full"
                    placeholder="15000 Ft"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    🥉 3. helyezés
                  </label>
                  <input
                    type="text"
                    value={form.thirdPrize}
                    onChange={(e) => setForm({ ...form, thirdPrize: e.target.value })}
                    className="input-dark w-full"
                    placeholder="5000 Ft"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-white">Játék beállítások</h4>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.doubleOut}
                      onChange={(e) => setForm({ ...form, doubleOut: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-cyan-500 focus:ring-cyan-500"
                    />
                    <span className="text-sm text-[#B8B8C3]">Double Out</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.doubleIn}
                      onChange={(e) => setForm({ ...form, doubleIn: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-cyan-500 focus:ring-cyan-500"
                    />
                    <span className="text-sm text-[#B8B8C3]">Double In</span>
                  </label>

                  {form.gameMode === 'cricket' && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.randomCricket}
                        onChange={(e) => setForm({ ...form, randomCricket: e.target.checked })}
                        className="w-4 h-4 rounded border-gray-300 text-cyan-500 focus:ring-cyan-500"
                      />
                      <span className="text-sm text-[#B8B8C3]">Random Cricket</span>
                    </label>
                  )}
                </div>
              </div>

            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={handleSave} className="btn-primary flex-1">
                {t('common.save')}
              </button>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditing(null);
                  resetForm();
                }}
                className="btn-secondary flex-1"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TournamentList;
